import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('burston_token'));
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
            
            // Fetch member profile if member
            if (response.data.role === 'member') {
                const profileRes = await axios.get(`${API}/member/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile(profileRes.data);
            }
        } catch (error) {
            console.error('Auth error:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        
        localStorage.setItem('burston_token', newToken);
        setToken(newToken);
        setUser(userData);
        
        // Fetch profile for members
        if (userData.role === 'member') {
            const profileRes = await axios.get(`${API}/member/profile`, {
                headers: { Authorization: `Bearer ${newToken}` }
            });
            setProfile(profileRes.data);
        }
        
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await axios.post(`${API}/auth/register`, { email, password, name });
        const { token: newToken, user: userData } = response.data;
        
        localStorage.setItem('burston_token', newToken);
        setToken(newToken);
        setUser(userData);
        
        // Fetch profile for members
        if (userData.role === 'member') {
            const profileRes = await axios.get(`${API}/member/profile`, {
                headers: { Authorization: `Bearer ${newToken}` }
            });
            setProfile(profileRes.data);
        }
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('burston_token');
        setToken(null);
        setUser(null);
        setProfile(null);
    };

    const refreshProfile = async () => {
        if (!token || !user || user.role !== 'member') return;
        
        try {
            const profileRes = await axios.get(`${API}/member/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfile(profileRes.data);
        } catch (error) {
            console.error('Profile refresh error:', error);
        }
    };

    const value = {
        user,
        token,
        profile,
        loading,
        login,
        register,
        logout,
        refreshProfile,
        isAuthenticated: !!user,
        isMember: user?.role === 'member',
        isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
        isManager: user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin',
        isSuperAdmin: user?.role === 'super_admin',
        // User type helpers
        userType: user?.user_type || 'residence_member',
        isResidenceMember: (user?.user_type || 'residence_member') === 'residence_member',
        isRetailShopper: user?.user_type === 'retail_shopper',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
